import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Seo from '../Seo';

const Teams = () => {
  const [isSivaExpanded, setIsSivaExpanded] = useState(false);
  const [isShreeExpanded, setIsShreeExpanded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen">
      <Seo
        title="Our Team"
        description="Meet the Libsys Inc leadership team — experienced IT consultants, engineers, and staffing experts driving enterprise transformation for clients across industries."
        path="/about-us/teams"
      />
      <section className="pt-8 pb-12 bg-slate-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-strategic-tagline text-tiny font-black uppercase tracking-[0.28em] block mb-3"
          >
            Leadership
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[30px] lg:text-[40px] font-black text-primary leading-tight mb-3"
          >
            Meet the Expert Team at Libsys
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-primary text-tiny font-black uppercase tracking-[0.2em] mb-2"
          >
            Management Team
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-slate-600 text-subtext font-medium leading-relaxed max-w-4xl"
          >
            Our management team combines deep technology knowledge with practical business execution while maintaining a strong focus on innovation, people, and long-term client success.
          </motion.p>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-16">
          {/* Siva Yarramsetty */}
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="group"
          >
            <div className="flex flex-col lg:flex-row gap-12 items-start">
              <div className="w-full lg:w-[400px] flex-shrink-0">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 shadow-sm group-hover:shadow-md transition-shadow">
                  <img
                    src="/Siva.jpg"
                    alt="Siva Yarramsetty"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="mt-4 text-center lg:text-left">
                  <p className="text-[#1a3a6b] text-tiny font-black uppercase tracking-[0.2em] mb-1">Founder & CEO</p>
                  <p className="text-slate-400 text-tiny font-bold uppercase tracking-widest">Libsys Inc.</p>
                </div>
              </div>

              <div className="flex-1 space-y-8">
                <div>
                  <h2 className="text-3xl font-black text-primary mb-2 tracking-tight">Meet Siva Yarramsetty, CEO & Founder of Libsys Inc.</h2>
                  <p className="text-[#1a3a6b] text-body font-black leading-tight italic opacity-80">
                    Leading with a Passion for Technology and a Balanced Life
                  </p>
                </div>

                <div className="space-y-6">
                  <p className="text-slate-600 text-subtext font-medium leading-relaxed">
                    Siva Yarramsetty, CEO and Founder of Libsys Inc., brings a unique blend of technical expertise and a dedication to well-being to his leadership role. With a strong track record in the IT industry (#ITServices), Siva Yarramsetty has guided Libsys to become a leader in Libsys' core services, Solar Monitoring, Solar Digital Analytics.
                  </p>

                  <div className="space-y-4">
                    <h3 className="text-primary text-subtext font-black uppercase tracking-wider">Beyond the Boardroom</h3>
                    <p className="text-slate-500 text-subtext font-medium leading-relaxed">
                      Siva Yarramsetty's commitment extends far beyond the boardroom. He is a passionate advocate for lifelong learning, reflected in his involvement as an #Entrepreneur, #Investor, and #Mentor. He is also actively involved in the #Crypto and #StartUps communities, fostering innovation and growth.
                      {!isSivaExpanded && (
                        <button 
                          onClick={() => setIsSivaExpanded(true)}
                          className="text-primary font-bold hover:underline ml-2 transition-colors inline-flex items-center"
                        >
                          View More
                        </button>
                      )}
                    </p>
                  </div>

                  <AnimatePresence>
                    {isSivaExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                        className="overflow-hidden space-y-6"
                      >
                        <div className="space-y-4 pt-4">
                          <h3 className="text-primary text-subtext font-black uppercase tracking-wider">Striking a Balance: The Many Sides of Siva Yarramsetty</h3>
                          <p className="text-slate-500 text-subtext font-medium leading-relaxed mb-4">
                            Beyond his leadership at Libsys, Siva Yarramsetty champions the importance of achieving a healthy work-life balance. He recognizes the need to manage stress and prioritize mental well-being. To achieve this balance, Siva Yarramsetty incorporates a variety of activities into his life:
                          </p>
                          <div className="space-y-2">
                            <p className="text-slate-600 text-subtext font-medium leading-relaxed"><span className="font-black text-primary">Martial Arts:</span> Siva Yarramsetty holds a black belt in Taekwondo, a discipline that has instilled in him focus, self-control, and physical fitness.</p>
                            <p className="text-slate-600 text-subtext font-medium leading-relaxed"><span className="font-black text-primary">Thrills on Two Wheels:</span> Motorcycle riding provides Siva Yarramsetty with a sense of freedom and adventure, allowing him to escape the daily grind and clear his head.</p>
                            <p className="text-slate-600 text-subtext font-medium leading-relaxed"><span className="font-black text-primary">Mind-Body Harmony:</span> Yoga and meditation practices like Pranayama or Tai Chi help Siva Yarramsetty achieve inner peace, reduce anxiety, and improve mental clarity.</p>
                            <p className="text-slate-600 text-subtext font-medium leading-relaxed"><span className="font-black text-primary">Giving Back:</span> Siva Yarramsetty is a regular blood donor, a testament to his commitment to social responsibility. Since 1992, he has selflessly donated blood 50 times, helping those in need.</p>
                          </div>
                          <p className="text-slate-500 text-subtext font-medium leading-relaxed">
                            By actively engaging in these diverse activities, Siva Yarramsetty cultivates a well-rounded lifestyle that fosters a composed state of mind, positive energy, and a strong network of like-minded individuals.
                          </p>
                        </div>

                        <div className="space-y-2">
                          <h3 className="text-primary text-subtext font-black uppercase tracking-wider">Connect with Siva Yarramsetty</h3>
                          <p className="text-slate-500 text-subtext font-medium leading-relaxed">
                            Siva Yarramsetty's story exemplifies the power of combining passion with purpose. His dedication to both his professional endeavors and personal well-being inspires those around him.
                          </p>
                        </div>

                        <div className="space-y-2 pt-2">
                          <h3 className="text-primary text-subtext font-black uppercase tracking-wider">A Life of Passion and Purpose</h3>
                          <p className="text-slate-500 text-subtext font-medium leading-relaxed">
                            Siva Yarramsetty is a thought leader who is always eager to connect with others. You can find him engaged in discussions on the latest advancements in technology and the importance of maintaining a balanced life.
                          </p>
                          <button 
                            onClick={() => setIsSivaExpanded(false)}
                            className="text-primary font-bold hover:underline text-tiny uppercase tracking-wider pt-2"
                          >
                            View Less
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </motion.article>

          <div className="h-px bg-slate-100 w-full" />

          {/* Shree Yerramsetti */}
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="group"
          >
            <div className="flex flex-col lg:flex-row-reverse gap-12 items-start">
              <div className="w-full lg:w-[400px] flex-shrink-0">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 shadow-sm group-hover:shadow-md transition-shadow">
                  <img
                    src="/Shree.jpg"
                    alt="Shree G Yerramsetti"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="mt-4 text-center lg:text-right">
                  <p className="text-[#1a3a6b] text-tiny font-black uppercase tracking-[0.2em] mb-1">Founder & President</p>
                  <p className="text-slate-400 text-tiny font-bold uppercase tracking-widest">Libsys Inc.</p>
                </div>
              </div>

              <div className="flex-1 space-y-8">
                <div>
                  <h2 className="text-3xl font-black text-primary mb-2 tracking-tight">Meet Shree Yerramsetti, President & Founder of Libsys Inc.</h2>
                </div>

                <div className="space-y-6">
                  <p className="text-slate-600 text-subtext font-medium leading-relaxed">
                    Shree Yerramsetti is a highly accomplished and goal-driven professional with over two decades of experience in IT Services, IT Staffing, and Solar Digital Monitoring. With expertise in sales leadership, marketing strategy, social selling, and predictive analytics, he has built and managed multi-million dollar enterprises. He is known for his strategic thinking and ability to deliver exceptional results.
                  </p>

                  <div className="space-y-2">
                    <p className="text-slate-600 text-subtext font-medium leading-relaxed"><span className="font-black text-primary">Accomplished Professional:</span> Over two decades of experience in IT Services, IT Staffing, and Solar Digital Monitoring.</p>
                    <p className="text-slate-600 text-subtext font-medium leading-relaxed"><span className="font-black text-primary">Strategic Leader:</span> Proven ability to build and manage multi-million dollar businesses through sales leadership, marketing strategy, social selling, and predictive analytics.</p>
                    <p className="text-slate-600 text-subtext font-medium leading-relaxed"><span className="font-black text-primary">Sustainability Champion:</span> Passionate advocate for sustainability, diversity and inclusion, and community involvement.</p>
                    <p className="text-slate-600 text-subtext font-medium leading-relaxed">
                      <span className="font-black text-primary">Industry Leader:</span> Stays up-to-date on trends and best practices, leveraging insights to create innovative solutions for business success.
                      {!isShreeExpanded && (
                        <button 
                          onClick={() => setIsShreeExpanded(true)}
                          className="text-primary font-bold hover:underline ml-2 transition-colors inline-flex items-center"
                        >
                          View More
                        </button>
                      )}
                    </p>
                  </div>

                  <AnimatePresence>
                    {isShreeExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                        className="overflow-hidden space-y-2"
                      >
                        <p className="text-slate-600 text-subtext font-medium leading-relaxed"><span className="font-black text-primary">Growth Catalyst:</span> Led multiple startups to exceed $10 million in revenue within five years.</p>
                        <p className="text-slate-600 text-subtext font-medium leading-relaxed"><span className="font-black text-primary">Technology Visionary:</span> Anticipates changes in the technology landscape and develops cutting-edge solutions in Solar Digital and Performance Monitoring.</p>
                        <p className="text-slate-600 text-subtext font-medium leading-relaxed"><span className="font-black text-primary">Passionate Speaker:</span> Shares knowledge and insights on startups, marketing, digital marketing, digital transformation, artificial intelligence, with a particular focus on solar, solar digital monitoring, and solar performance.</p>
                        <p className="text-slate-600 text-subtext font-medium leading-relaxed"><span className="font-black text-primary">Emphasize adventure:</span> Shree Yerramsetti craves adventure, and motorcycle riding is his ticket to explore, venturing beyond the routine and discovering new horizons on every ride.</p>
                        <button 
                          onClick={() => setIsShreeExpanded(false)}
                          className="text-primary font-bold hover:underline text-tiny uppercase tracking-wider pt-2"
                        >
                          View Less
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </motion.article>
        </div>
      </section>
    </div>
  );
};

export default Teams;
